import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Info } from 'lucide-react'
import {
  UNAVAILABLE_TEXT,
  formatDate,
  formatMetric,
  healthBadge,
  humanize,
  isMetric
} from './managementUtils'

export const HealthBadge = ({ status, label }) => {
  const b = healthBadge(status, label)
  return <span className={b.className}>{b.label}</span>
}

export const MetricValue = ({ metric, format }) => {
  const f = formatMetric(metric, format)
  return (
    <span className={f.available ? 'mis-value' : 'mis-value mis-unavailable'} title={f.note || undefined}>
      {f.text}
    </span>
  )
}

export const KpiTile = ({ label, metric, format = 'number', hint, to, tone }) => {
  const f = formatMetric(metric, format)
  const body = (
    <>
      <div className="mis-kpi-label">{label}</div>
      <div className={`mis-kpi-value ${f.available ? '' : 'mis-unavailable'}`}>{f.text}</div>
      {(f.note || hint) && (
        <div className="mis-kpi-note" title={f.note || hint}>{f.note || hint}</div>
      )}
      {to && <ArrowUpRight size={14} className="mis-kpi-link-icon" />}
    </>
  )
  const cls = `mis-kpi ${f.available ? '' : 'mis-kpi-grey'} ${tone ? `mis-kpi-${tone}` : ''}`
  return to ? <Link to={to} className={`${cls} mis-kpi-link`}>{body}</Link> : <div className={cls}>{body}</div>
}

export const KpiGrid = ({ children }) => <div className="mis-kpi-grid">{children}</div>

export const Section = ({ title, subtitle, actions, children, className = '' }) => (
  <section className={`mis-section ${className}`}>
    {(title || actions) && (
      <div className="mis-section-header">
        <div>
          {title && <h3>{title}</h3>}
          {subtitle && <p>{subtitle}</p>}
        </div>
        {actions && <div className="mis-section-actions">{actions}</div>}
      </div>
    )}
    <div className="mis-section-body">{children}</div>
  </section>
)

export const DrillLinks = ({ links = [] }) => (
  <div className="mis-drill-links no-print">
    {links.map((l) => (
      <Link key={l.to} to={l.to} className="mis-drill-link">
        {l.label} <ArrowUpRight size={13} />
      </Link>
    ))}
  </div>
)

export const Unavailable = ({ note, children }) => (
  <div className="mis-empty">
    <Info size={16} />
    <span>{children || UNAVAILABLE_TEXT}{note ? ` — ${note}` : ''}</span>
  </div>
)

const renderCell = (value, col, row) => {
  if (col.render) return col.render(value, row)
  if (isMetric(value) || (col.format && typeof value === 'number')) return <MetricValue metric={value} format={col.format} />
  if (value === null || value === undefined || value === '') return <span className="mis-muted">—</span>
  if (col.type === 'date') return formatDate(value)
  if (col.type === 'health') return <HealthBadge status={value} />
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return value.name || value.label || JSON.stringify(value)
  return String(value)
}

export const DataTable = ({ columns, rows, emptyText = 'No records for the selected period.', maxRows = 25, rowKey }) => {
  const [expanded, setExpanded] = useState(false)
  if (!Array.isArray(rows) || rows.length === 0) {
    return <Unavailable>{emptyText}</Unavailable>
  }
  const visible = expanded ? rows : rows.slice(0, maxRows)
  return (
    <div className="mis-table-wrap">
      <table className="mis-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.align === 'right' || c.format ? 'text-end' : ''}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row, i) => (
            <tr key={rowKey ? rowKey(row, i) : row._id || row.id || i}>
              {columns.map((c) => (
                <td key={c.key} className={c.align === 'right' || c.format ? 'text-end' : ''}>
                  {renderCell(c.get ? c.get(row) : row[c.key], c, row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > maxRows && (
        <button type="button" className="btn btn-link btn-sm no-print" onClick={() => setExpanded((e) => !e)}>
          {expanded ? 'Show fewer' : `Show all ${rows.length} rows`}
        </button>
      )}
    </div>
  )
}

// ---------- generic renderer for payloads with unknown shape ----------

const SKIP_KEYS = new Set(['_id', '__v', 'company', 'period', 'createdBy', 'updatedBy'])
const CURRENCY_HINT = /(amount|value|cost|revenue|cash|balance|burn|budget|outstanding|overdue|invoiced|taxable|collected|received|cleared|paid|inflow|outflow|net|margin|pipeline|commitment|sow|billed|notDue|over90|variance)(inr)?$/i
const PERCENT_HINT = /percent$/i

export const guessFormat = (key = '') => {
  if (PERCENT_HINT.test(key)) return 'percent'
  if (/months$/i.test(key)) return 'months'
  if (/days$/i.test(key) && !/count/i.test(key)) return 'days'
  if (/hours$/i.test(key)) return 'hours'
  if (/count$|headcount|employees|projects$|allocated|underAllocated|overAllocated|^open$|^red$|^amber$|^total$/i.test(key)) return 'number'
  if (CURRENCY_HINT.test(key)) return 'currency'
  return 'number'
}

const autoColumns = (rows) => {
  const keys = []
  rows.slice(0, 5).forEach((r) => Object.keys(r || {}).forEach((k) => {
    if (!keys.includes(k) && !SKIP_KEYS.has(k) && !/Id$/.test(k)) keys.push(k)
  }))
  return keys.slice(0, 8).map((k) => {
    const sample = rows.find((r) => r?.[k] !== undefined && r?.[k] !== null)?.[k]
    const col = { key: k, label: humanize(k) }
    if (isMetric(sample)) col.format = guessFormat(k)
    else if (/date$|At$/i.test(k)) col.type = 'date'
    else if (/health|severity/i.test(k)) col.type = 'health'
    else if (sample && typeof sample === 'object' && !Array.isArray(sample) && !('name' in sample)) {
      col.render = (v) => (v && typeof v === 'object'
        ? Object.entries(v).filter(([, x]) => isMetric(x)).map(([kk, x]) => `${humanize(kk)}: ${formatMetric(x, guessFormat(kk)).text}`).join(' · ') || '—'
        : '—')
    }
    return col
  })
}

/** Renders any MIS payload: metrics → tiles, arrays → tables, objects → sub-sections. */
export const AutoSection = ({ data, depth = 0 }) => {
  if (isMetric(data)) return <KpiGrid><KpiTile label="Value" metric={data} /></KpiGrid>
  if (!data || typeof data !== 'object') return <Unavailable />
  const entries = Object.entries(data).filter(([k]) => !SKIP_KEYS.has(k))
  const metrics = entries.filter(([, v]) => isMetric(v))
  const arrays = entries.filter(([, v]) => Array.isArray(v))
  const objects = entries.filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v) && !isMetric(v) && !(v instanceof Date))
  const scalars = entries.filter(([, v]) => v !== null && typeof v !== 'object')

  if (!metrics.length && !arrays.length && !objects.length && !scalars.length) return <Unavailable />

  return (
    <div className={`mis-auto depth-${depth}`}>
      {(metrics.length > 0 || scalars.length > 0) && (
        <KpiGrid>
          {metrics.map(([k, v]) => <KpiTile key={k} label={humanize(k)} metric={v} format={guessFormat(k)} />)}
          {scalars.map(([k, v]) => (
            <div key={k} className="mis-kpi">
              <div className="mis-kpi-label">{humanize(k)}</div>
              <div className="mis-kpi-value mis-kpi-text">{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</div>
            </div>
          ))}
        </KpiGrid>
      )}
      {depth < 3 && objects.map(([k, v]) => (
        <div key={k} className="mis-auto-group">
          <h4>{humanize(k)}</h4>
          <AutoSection data={v} depth={depth + 1} />
        </div>
      ))}
      {arrays.map(([k, v]) => (
        <div key={k} className="mis-auto-group">
          <h4>{humanize(k)}</h4>
          {v.length && typeof v[0] === 'object'
            ? <DataTable columns={autoColumns(v)} rows={v} maxRows={10} />
            : v.length ? <p>{v.join(', ')}</p> : <Unavailable>No records</Unavailable>}
        </div>
      ))}
    </div>
  )
}

export const Loading = ({ label = 'Loading management data…' }) => (
  <div className="mis-loading">
    <div className="spinner-border text-primary" role="status" />
    <p>{label}</p>
  </div>
)

export const ErrorState = ({ message, onRetry }) => (
  <div className="alert alert-warning mis-error">
    <div>{message}</div>
    {onRetry && <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRetry}>Retry</button>}
  </div>
)
